/**
 * ThemeProvider - Inject CSS variables dari config ke root element
 */
import { useConfig } from "@/context/ConfigContext";
import { useEffect } from "react";

const FALLBACK_FONT_STACK =
  "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, \"Segoe UI\", Roboto, \"Helvetica Neue\", Arial, \"Noto Sans\", sans-serif, \"Apple Color Emoji\", \"Segoe UI Emoji\", \"Segoe UI Symbol\", \"Noto Color Emoji\"";

const STOREFRONT_FONTS = {
  Inter: "Inter",
  "Plus Jakarta Sans": "Plus+Jakarta+Sans",
  Poppins: "Poppins",
  Roboto: "Roboto",
  Manrope: "Manrope",
  "DM Sans": "DM+Sans",
};

export default function ThemeProvider({ children }) {
  const { config, isConfigReady } = useConfig();

  useEffect(() => {
    if (!isConfigReady || !config?.theme?.colors) {
      return;
    }

    const root = document.documentElement;
    const colors = config.theme.colors;

    const primaryColor = toSafeHex(colors.primary, "#0066FF");
    const secondaryColor = toSafeHex(colors.secondary, primaryColor);
    const accentColor = toSafeHex(colors.accent, primaryColor);
    const backgroundColor = toSafeHex(colors.background, "#ffffff");
    const surfaceColor = toSafeHex(colors.surface, "#ffffff");
    const textColor = toSafeHex(colors.text, "#111827");
    const fonts = config.theme.fonts || {};
    const primaryFontName = resolveStorefrontFont(fonts.primary, "Inter");
    const secondaryFontName = resolveStorefrontFont(
      fonts.secondary,
      primaryFontName,
    );
    const primaryFont = toSafeFontStack(
      primaryFontName,
      FALLBACK_FONT_STACK,
    );
    const secondaryFont = toSafeFontStack(secondaryFontName, primaryFont);

    loadStorefrontFonts([primaryFontName, secondaryFontName]);

    // Base colors
    root.style.setProperty("--color-primary", primaryColor);
    root.style.setProperty("--color-secondary", secondaryColor);
    root.style.setProperty("--color-accent", accentColor);
    root.style.setProperty("--color-background", backgroundColor);
    root.style.setProperty("--color-surface", surfaceColor);
    root.style.setProperty("--color-text", textColor);
    root.style.setProperty("--font-fallback", FALLBACK_FONT_STACK);
    root.style.setProperty("--font-primary", primaryFont);
    root.style.setProperty("--font-secondary", secondaryFont);

    if (colors.danger) {
      root.style.setProperty("--color-danger", colors.danger);
    }
    if (colors.warning) {
      root.style.setProperty("--color-warning", colors.warning);
    }
    if (colors.info) {
      root.style.setProperty("--color-info", colors.info);
    }

    // Light/dark variants
    root.style.setProperty(
      "--color-primary-light",
      lightenColor(primaryColor, 0.2),
    );
    root.style.setProperty(
      "--color-primary-dark",
      darkenColor(primaryColor, 0.2),
    );

    root.style.setProperty(
      "--color-secondary-light",
      lightenColor(secondaryColor, 0.2),
    );
    root.style.setProperty(
      "--color-secondary-dark",
      darkenColor(secondaryColor, 0.2),
    );

    root.style.setProperty(
      "--color-accent-light",
      lightenColor(accentColor, 0.2),
    );
    root.style.setProperty(
      "--color-accent-dark",
      darkenColor(accentColor, 0.2),
    );

    // Generate palette dari primary color dan mapping ke nevada
    const primaryPalette = generateColorPalette(primaryColor);
    setColorPaletteVariables(root, "primary", primaryPalette);
    setColorPaletteVariables(root, "nevada", primaryPalette);

    return () => {
      // Optional cleanup
    };
  }, [config, isConfigReady]);

  return <>{children}</>;
}

function resolveStorefrontFont(value, fallback) {
  const font = typeof value === "string" ? value.trim() : "";
  return Object.hasOwn(STOREFRONT_FONTS, font) ? font : fallback;
}

function loadStorefrontFonts(fontNames) {
  const families = [...new Set(fontNames)]
    .filter((font) => Object.hasOwn(STOREFRONT_FONTS, font))
    .map(
      (font) =>
        `family=${STOREFRONT_FONTS[font]}:wght@400;500;600;700`,
    );

  if (families.length === 0) return;

  ensureResourceHint("preconnect", "https://fonts.googleapis.com");
  ensureResourceHint(
    "preconnect",
    "https://fonts.gstatic.com",
    "anonymous",
  );

  const href = `https://fonts.googleapis.com/css2?${families.join(
    "&",
  )}&display=swap`;
  let stylesheet = document.getElementById("estore-dynamic-fonts");

  if (!stylesheet) {
    stylesheet = document.createElement("link");
    stylesheet.id = "estore-dynamic-fonts";
    stylesheet.rel = "stylesheet";
    document.head.appendChild(stylesheet);
  }

  if (stylesheet.href !== href) {
    stylesheet.href = href;
  }
}

function ensureResourceHint(rel, href, crossOrigin) {
  const selector = `link[rel="${rel}"][href="${href}"]`;
  if (document.head.querySelector(selector)) return;

  const link = document.createElement("link");
  link.rel = rel;
  link.href = href;
  if (crossOrigin) link.crossOrigin = crossOrigin;
  document.head.appendChild(link);
}

function toSafeFontStack(font, fallback) {
  const value = typeof font === "string" ? font.trim() : "";
  if (!value) return fallback;

  const cleaned = value
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const unquoted = part.replace(/^['"]|['"]$/g, "").trim();
      if (!unquoted || /[;{}<>]/.test(unquoted)) return "";
      if (/^(inherit|initial|unset|revert)$/i.test(unquoted)) return "";
      if (/^(serif|sans-serif|monospace|cursive|fantasy|system-ui|ui-sans-serif|ui-serif|ui-monospace|emoji|math|fangsong)$/i.test(unquoted)) {
        return unquoted;
      }
      return /[\s]/.test(unquoted) ? `"${unquoted.replace(/"/g, "")}"` : unquoted;
    })
    .filter(Boolean)
    .join(", ");

  return cleaned ? `${cleaned}, ${fallback}` : fallback;
}

function lightenColor(color, amount) {
  return mixColors(color, "#ffffff", amount);
}

function darkenColor(color, amount) {
  return mixColors(color, "#000000", amount);
}

const shadeAdjustments = {
  50: 0.97,
  100: 0.8,
  200: 0.65,
  300: 0.5,
  400: 0.3,
  500: 0,
  600: -0.2,
  700: -0.3,
  800: -0.5,
  900: -0.8,
  950: -0.9,
};

function generateColorPalette(baseColor) {
  const palette = {};
  Object.entries(shadeAdjustments).forEach(([shade, adjustment]) => {
    if (adjustment === 0) {
      palette[shade] = baseColor;
      return;
    }

    palette[shade] =
      adjustment > 0
        ? mixColors(baseColor, "#ffffff", adjustment)
        : mixColors(baseColor, "#000000", Math.abs(adjustment));
  });
  return palette;
}

function setColorPaletteVariables(root, name, palette) {
  Object.entries(palette).forEach(([shade, value]) => {
    root.style.setProperty(`--color-${name}-${shade}`, value);
  });
}

function mixColors(color, targetColor, amount) {
  const source = hexToRgb(color);
  const target = hexToRgb(targetColor);

  const r = Math.round(source.r + (target.r - source.r) * amount);
  const g = Math.round(source.g + (target.g - source.g) * amount);
  const b = Math.round(source.b + (target.b - source.b) * amount);

  return rgbToHex(r, g, b);
}

function hexToRgb(hex) {
  let normalized = hex.replace("#", "");
  if (normalized.length === 3) {
    normalized = normalized
      .split("")
      .map((char) => char + char)
      .join("");
  }

  const num = parseInt(normalized, 16);
  return {
    r: (num >> 16) & 255,
    g: (num >> 8) & 255,
    b: num & 255,
  };
}

function toSafeHex(color, fallback) {
  if (typeof color !== "string" || color.trim() === "") {
    return fallback;
  }

  const normalized = color.trim();
  if (/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(normalized)) {
    return normalized;
  }
  if (/^(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(normalized)) {
    return `#${normalized}`;
  }

  return fallback;
}

function rgbToHex(r, g, b) {
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}
